import React, { useState, useEffect } from 'react';
import { Box, Drawer, Typography, Slider } from '@mui/material';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import CustomTextField from '../../../../../../common/components/customized/customtextfield/CustomTextField';
import CustomButton from '../../../../../../common/components/buttons/CustomButton';
import CustomSelect from '../../../../../../common/components/customized/customselect/CustomSelect';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomMultiSelect from '../../../../../../common/components/customized/custommultiselect/CustomMultiSelect';
import CustomRadioButton from '../../../../../../common/components/customized/customradiobutton/CustomRadioButton';
import {
  imeiDropdownAction,
  planIdDropdownAction,
  simDropdownAction,
  vehicleMakeDropdownAction,
  vehicleModelDropdownAction,
  vehicleOnboardAction,
  vehicleUpdateAction
} from './vehicleOnboard';
import { useAppDispatch, useAppSelector } from '../../../../../../app/redux/hooks';
import { autoGetVechicleAction } from '../../../../redux/reducer/autoPlannerSlices/autoplannerConfigurationSlice';

interface AddVehicleProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedRow?: any;
  payloads: any;
}

const AddVehicle = ({
  setOpen,
  open,
  selectedRow,
  payloads
}: AddVehicleProps): JSX.Element => {
  const [seatCapacity, setSeatCapacity] = useState<number>(0);
  const [absoluteSeating, setAbsoluteSeating] = useState<number>(0);
  const [istracking, setistracking] = useState<any>('notIstracking');
  const [vehicleMake, setVehicleMake] = useState<any>('');
  const [vehicleMakeDrop, setVehicleMakeDrop] = useState<any>([]);
  const [vehicleModelDrop, setVehicleModelDrop] = useState<any>([]);
  const [year, setYear] = useState<any>([]);
  const [subtypeDropdown, setSubtypeDropdown] = useState<any>([]);
  const [vehicleModel, setVehicleModel] = useState<any>('');
  const [manufacturedYear, setManufacturedYear] = useState<any>('');
  const [freewaySpeedLimit, setFreewaySpeedLimit] = useState(90.0);
  const [nonFreewaySpeedLimit, setNonFreewaySpeedLimit] = useState(70.0);

  const IMEI = useAppSelector(state => state.dropdownIMEI.data || []);
  const AddVehi = useAppSelector(state => state.onboardVehicle.isLoading);
  const vehiUpdateLoading = useAppSelector(state => state.updateVehi.isLoading);
  const planID = useAppSelector(state => state.dropdownPlanID.data || []);
  const SIM = useAppSelector(state => state.dropdownSIM.data || []);
  const VehicleMakeApi = useAppSelector(state => state.dropdownVehicleMake?.data || []);
  const model = useAppSelector(state => state.dropdownVehicleModel?.data || []);

  const validationSchema = yup.object().shape({
    vehicleNumber: yup
      .string()
      .required('Enter vehicle number')
      .matches(/^[A-Za-z0-9]*$/, 'Only letters and numbers allowed without spaces')
      .test(
        'valid-length',
        'Enter a valid vehicle number',
        value => (value?.length || 0) >= 4
      )
      .max(20, 'Maximum 20 characters')
      .test('has-letter', 'Must include at least one letter', value =>
        /[A-Za-z]/.test(value || '')
      ),

    istracking: yup.string().optional(),

    imeiNumber: yup.string().when('istracking', {
      is: 'istracking',
      then: schema => schema.required('Select IMEI number'),
      otherwise: schema => schema.notRequired()
    }),

    simNumber: yup.string().when('istracking', {
      is: 'istracking',
      then: schema =>
        schema
          .required('Select SIM number')
          .matches(/^\d{10,20}$/, 'SIM number must be between 10-20 digits'),
      otherwise: schema => schema.notRequired()
    }),

    freewaySpeedLimit: yup.number().when('istracking', {
      is: 'istracking',
      then: schema =>
        schema
          .min(60, 'Freeway speed limit must be at least 60 km/h')
          .max(150, 'Freeway speed limit cannot exceed 150 km/h')
          .required('Freeway speed limit is required'),
      otherwise: schema => schema.notRequired()
    }),

    nonFreewaySpeedLimit: yup.number().when('istracking', {
      is: 'istracking',
      then: schema =>
        schema
          .min(30, 'Non-freeway speed limit must be at least 30 km/h')
          .max(100, 'Non-freeway speed limit cannot exceed 100 km/h')
          .required('Non-freeway speed limit is required'),
      otherwise: schema => schema.notRequired()
    }),
    planId: yup.string().when('istracking', {
      is: 'istracking',
      then: schema => schema.required('Select plan ID'),
      otherwise: schema => schema.notRequired()
    }),

    vehicleMake: yup.string().when('istracking', {
      is: 'istracking',
      then: schema => schema.required('Select vehicle make'),
      otherwise: schema => schema.notRequired()
    }),

    vehicleModel: yup.string().when('istracking', {
      is: 'istracking',
      then: schema => schema.required('Select vehicle model'),
      otherwise: schema => schema.notRequired()
    }),
    manufacturedYear: yup.string().notRequired(),

    subType: yup.string().when('istracking', {
      is: 'istracking',
      then: schema => schema.required('Select sub type'),
      otherwise: schema => schema.notRequired()
    }),

    tripMode: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().required(),
          label: yup.string().required()
        })
      )
      .min(1, 'Select atleast one tour mode')
      .required('Select tour mode'),
    vehicleType: yup.string().required('Select vehicle type'),
    averageSpeed: yup
      .number()
      .min(10, 'Minimum average speed is 10 km/h')
      .max(150, 'Maximum average speed is 150 km/h')
      .required('Average speed is required')
      .typeError('Enter valid averageSpeed')
      .test(
        'less-than-overspeed',
        'Average speed must be less than overspeed limit',
        function (value) {
          const { overspeedLimit } = this.parent;
          return !value || !overspeedLimit || value < overspeedLimit;
        }
      ),
    absoluteSeating: yup
      .number()
      .required('Enter absolute seating')
      .min(1, 'Minimum absolute seating is 1')
      .max(249, 'Maximum absolute seating is 249')
      .transform((value, originalValue) => {
        return originalValue === '' ? undefined : value;
      })
      .typeError('Enter valid absolute seating'),
    seating: yup
      .number()
      .transform((value, originalValue) => {
        return originalValue === '' ? undefined : value;
      })
      .required('Enter preferred seating')
      .min(1, 'Minimum preferred seating is 1')
      .test(
        'maximum-seats',
        `Maximum preferred seating is ${absoluteSeating ? absoluteSeating : 0}`,
        (value: any) => {
          return absoluteSeating > 0 ? value <= absoluteSeating : true;
        }
      )
  });

  const imeiNumber = IMEI?.map((value: any) => ({
    id: value,
    label: value
  }));

  const planIdDropdown = planID?.map((value: any) => ({
    id: value,
    label: value
  }));

  const simNumber = SIM?.map((value: any) => ({
    id: value,
    label: value
  }));

  const dropdownMake = vehicleMakeDrop?.map((value: any) => ({
    id: value,
    label: value
  }));

  const yearDrop = year?.map((value: any) => ({
    id: value,
    label: value
  }));

  const vehiModel = vehicleModelDrop?.map((value: any) => ({
    id: value,
    label: value
  }));

  const subtype = subtypeDropdown?.map((value: any) => ({
    id: value,
    label: value
  }));

  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    getValues,
    resetField,
    trigger,
    setValue,
    reset,

    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      averageSpeed: 60,
      freewaySpeedLimit: 90,
      nonFreewaySpeedLimit: 70
    },
    resolver: yupResolver(validationSchema)
  });
  const averageSpeed = watch('averageSpeed', 60);

  const vehicleType = [
    { id: 'coach', label: 'Coach' },
    { id: 'bus', label: 'Bus' },
    { id: 'mini van', label: 'Mini van' },
    { id: 'comfy', label: 'Comfy' }
  ];

  const mode = [
    {
      id: 'selectAll',
      label: 'Select All',
      checked: false
    },
    { id: 'PVT', label: 'PVT' },
    { id: 'SIC', label: 'SIC' },
    { id: 'TSIC', label: 'TSIC' },
    { id: 'GRP', label: 'GRP' },
    { id: 'RLR', label: 'RLR' },
    { id: 'TRANSFER', label: 'TRANSFER' }
  ];

  const handleClose = () => {
    reset();
    setOpen(false);
    setistracking('notIstracking');
  };

  const onSubmit = async (data: any) => {
    const payload = {
      vehicleType: data.vehicleType,
      vehicleNumber: data.vehicleNumber.toUpperCase(),
      standardSpeedLimit: data.averageSpeed,
      seatingPercentage: seatCapacity,
      overSpeedLimit: null,
      isTracking: istracking === 'istracking' ? true : false,
      absoluteSeating: data.absoluteSeating,
      tracking: {
        ...(istracking === 'istracking' && {
          freeWaySpeedLimit: data.freewaySpeedLimit || null,
          nonFreewaySpeedLimit: data.nonFreewaySpeedLimit || null,
          vehicleCategory: null,
          imeiNumber: data.imeiNumber || null,
          simNumber: data.simNumber || null,
          planId: data.planId || null,
          vehicleMake: data.vehicleMake || null,
          vehicleModel: data.vehicleModel || null,
          manufacturedYear: data.manufacturedYear || null,
          subType: data.subType || null
        })
      },
      preferredSeating: data.seating,
      tripModes: data.tripMode
        .filter((mode: any) => mode.id !== 'selectAll')
        .map((mode: any) => mode.id)
    };

    const addPayload = {
      vehicleType: data.vehicleType,
      vehicleNumber: data.vehicleNumber.toUpperCase(),
      standardSpeedLimit: data.averageSpeed,
      seatingPercentage: seatCapacity,
      overSpeedLimit: null,
      isTracking: istracking === 'istracking' ? true : false,
      absoluteSeating: data.absoluteSeating,
      freeWaySpeedLimit: data.freewaySpeedLimit || null,
      nonFreewaySpeedLimit: data.nonFreewaySpeedLimit || null,
      vehicleCategory: null,
      imeiNumber: data.imeiNumber || null,
      simNumber: data.simNumber || null,
      planId: data.planId || null,
      vehicleMake: data.vehicleMake || null,
      vehicleModel: data.vehicleModel || null,
      manufacturedYear: data.manufacturedYear || null,
      subType: data.subType || null,
      preferredSeating: data.seating,
      tripModes: data.tripMode
        .filter((mode: any) => mode.id !== 'selectAll')
        .map((mode: any) => mode.id)
    };
    if (selectedRow) {
      const actionUpdate = await dispatch(vehicleUpdateAction(payload));
      if (actionUpdate.type === vehicleUpdateAction.fulfilled.type) {
        handleClose();
        dispatch(autoGetVechicleAction(payloads));
      }
    } else {
      const action = await dispatch(vehicleOnboardAction(addPayload));
      if (action.type === vehicleOnboardAction.fulfilled.type) {
        handleClose();
        dispatch(autoGetVechicleAction(payloads));
      }
    }
  };

  const handleFreewaySpeedChange = (newValue: any) => {
    setFreewaySpeedLimit(newValue);
    setValue('freewaySpeedLimit', newValue);

    trigger('freewaySpeedLimit');
  };

  const handleNonFreewaySpeedChange = (newValue: any) => {
    setNonFreewaySpeedLimit(newValue);
    setValue('nonFreewaySpeedLimit', newValue);

    trigger('nonFreewaySpeedLimit');
  };

  useEffect(() => {
    const payload = {
      vehicleNumber: selectedRow?.vehicleNumber || ''
    };
    dispatch(imeiDropdownAction(payload));
    dispatch(simDropdownAction(payload));
    dispatch(planIdDropdownAction());
    dispatch(vehicleMakeDropdownAction());
  }, []);

  useEffect(() => {
    if (VehicleMakeApi?.vehicleMake) {
      setVehicleMakeDrop(VehicleMakeApi?.vehicleMake);
    }
  }, [VehicleMakeApi?.vehicleMake]);

  useEffect(() => {
    if (model?.vehicleModel) {
      setVehicleModelDrop(model?.vehicleModel);
    }
  }, [model]);

  useEffect(() => {
    if (model?.manufacturedYear) {
      setYear(model?.manufacturedYear);
    }
  }, [model]);

  useEffect(() => {
    if (model?.subType) {
      setSubtypeDropdown(model?.subType);
    }
  }, [model]);

  useEffect(() => {
    console.log('selectedRow', selectedRow);
    if (selectedRow?.id) {
      setValue('vehicleNumber', selectedRow?.vehicleNumber);
      const seatingPercentage = Math.round(
        (selectedRow?.preferredSeating / selectedRow?.absoluteSeating) * 100
      );
      const tripModes = selectedRow?.tripModes?.map((mode: string) => ({
        id: mode,
        label: mode
      }));
      setistracking(selectedRow.isTracking ? 'istracking' : 'notIstracking');
      setValue('istracking', selectedRow.isTracking ? 'istracking' : 'notIstracking');
      setValue('tripMode', tripModes);
      setValue('vehicleType', selectedRow?.vehicleType);
      setValue('imeiNumber', selectedRow?.imeiNumber || '');
      setValue('simNumber', selectedRow?.simNumber || '');
      setValue('planId', selectedRow?.planId || '');
      setValue('vehicleMake', selectedRow?.vehicleMake || '');
      setValue('vehicleModel', selectedRow?.vehicleModel || '');
      setValue('manufacturedYear', selectedRow?.manufacturedYear || '');
      setValue('subType', selectedRow?.subType || '');
      trigger('tripMode');
      setValue('absoluteSeating', selectedRow?.absoluteSeating);
      setValue('seating', selectedRow?.preferredSeating);
      setSeatCapacity(seatingPercentage);
      setAbsoluteSeating(selectedRow?.absoluteSeating);
      setValue('averageSpeed', selectedRow?.standardSpeedLimit);
    } else {
      setValue('vehicleNumber', '');
      setValue('tripMode', []);
      setValue('vehicleType', '');
    }
  }, [selectedRow, open]);

  useEffect(() => {
    if (selectedRow) {
      const payload = {
        vehicleMake: selectedRow?.vehicleMake || ''
      };
      dispatch(vehicleModelDropdownAction(payload));
      const payloads = {
        vehicleMake: selectedRow.vehicleMake || '',
        vehicleModel: selectedRow.vehicleModel || ''
      };
      dispatch(vehicleModelDropdownAction(payloads));
      const payloadss = {
        vehicleMake: selectedRow.vehicleMake || '',
        vehicleModel: selectedRow.vehicleModel || '',
        manufacturedyear: selectedRow.manufacturedYear || ''
      };
      dispatch(vehicleModelDropdownAction(payloadss));
    }
  }, [selectedRow]);

  return (
    <Box p={2}>
      <Drawer anchor='right' open={open} onClose={handleClose} sx={{ zIndex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='h6' sx={{ fontWeight: 700, mb: 3 }}>
            {selectedRow ? 'Update vehicle' : ' Add Vehicle'}
          </Typography>
          <Box component='form' onSubmit={handleSubmit(onSubmit)}>
            <CustomTextField
              control={control}
              name='vehicleNumber'
              placeholder='Vehicle number'
              id='vehicleNumber'
              label='Vehicle Number'
              disabled={selectedRow}
              maxlength={20}
            />

            <>
              <CustomRadioButton
                name='istracking'
                control={control}
                formlabel='Is Tracking Enabled?'
                radiobutton1='No'
                radiobutton2='Yes'
                // value={getValues('istracking')}
                value1='notIstracking'
                value2='istracking'
                value={istracking}
                onChangeCallback={e => {
                  setistracking(e?.target?.value);
                  setValue('istracking', e?.target?.value);
                }}
              />
            </>

            {istracking === 'istracking' ? (
              <>
                <CustomSelect
                  id='imeiNumber'
                  control={control}
                  name='imeiNumber'
                  label='IMEI Number'
                  placeholder='Select IMEI Number'
                  options={imeiNumber}
                />

                <CustomSelect
                  id='simNumber'
                  control={control}
                  name='simNumber'
                  label='SIM Number'
                  placeholder='Select SIM Number'
                  options={simNumber}
                />

                <CustomSelect
                  id='planId'
                  control={control}
                  name='planId'
                  label='Plan Id'
                  placeholder='Select Plan Id'
                  options={planIdDropdown}
                />
                <CustomSelect
                  id='vehicleMake'
                  control={control}
                  name='vehicleMake'
                  label='Vehicle Make'
                  placeholder='Select Vehicle Make'
                  options={dropdownMake}
                  onChanges={(e: any, newValue: any) => {
                    setVehicleMake(newValue.label);
                    const payload = {
                      vehicleMake: newValue.label
                    };
                    dispatch(vehicleModelDropdownAction(payload));
                  }}
                />

                <CustomSelect
                  id='vehicleModel'
                  control={control}
                  name='vehicleModel'
                  label='Vehicle Model'
                  placeholder='Select Vehicle Model'
                  options={vehiModel}
                  onChanges={(e: any, newValue: any) => {
                    setVehicleModel(newValue.label);
                    const payload = {
                      vehicleMake: vehicleMake,
                      vehicleModel: newValue.label
                    };
                    dispatch(vehicleModelDropdownAction(payload));
                  }}
                />
                <CustomSelect
                  id='manufacturedYear'
                  control={control}
                  isOptional={true}
                  name='manufacturedYear'
                  label='Manufactured Year'
                  placeholder='Select Manufactured Year'
                  options={yearDrop}
                  onChanges={(e: any, newValue: any) => {
                    const payload = {
                      vehicleMake: vehicleMake,
                      vehicleModel: vehicleModel,
                      manufacturedyear: newValue.label
                    };
                    dispatch(vehicleModelDropdownAction(payload));
                  }}
                />
                <CustomSelect
                  id='subType'
                  control={control}
                  name='subType'
                  label='Sub Type'
                  placeholder='Select Sub Type'
                  options={subtype}
                />

                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: '1px solid #dee2e6',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <Typography
                    variant='subtitle2'
                    sx={{
                      fontWeight: 600,
                      color: '#495057',
                      fontSize: '13px',
                      mb: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    🛣️ Freeway Speed Configuration
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 2 }}>
                    <Box sx={{ minWidth: 100 }}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 700,
                          color: '#6c757d',
                          mb: 0.5
                        }}
                      >
                        Freeway Limit
                      </Typography>
                      <Box
                        sx={{
                          background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                          borderRadius: '8px',
                          px: 1.5,
                          py: 0.3,
                          display: 'inline-block'
                        }}
                      >
                        <Typography
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}
                        >
                          {freewaySpeedLimit} km/h
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Slider
                        value={freewaySpeedLimit}
                        onChange={(_, newValue) =>
                          handleFreewaySpeedChange(newValue as number)
                        }
                        min={60}
                        max={150}
                        step={5}
                        size='small'
                        sx={{
                          '& .MuiSlider-track': {
                            background:
                              'linear-gradient(90deg, #28a745 0%, #20c997 100%)',
                            border: 'none'
                          },
                          '& .MuiSlider-thumb': {
                            backgroundColor: '#28a745',
                            width: 16,
                            height: 16,
                            '&:hover': {
                              boxShadow: '0 0 0 8px rgba(40, 167, 69, 0.16)'
                            }
                          }
                        }}
                      />
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}
                      >
                        <Typography sx={{ fontSize: '9px', color: '#adb5bd' }}>
                          60
                        </Typography>
                        <Typography sx={{ fontSize: '9px', color: '#adb5bd' }}>
                          150
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ minWidth: 100 }}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 700,
                          color: '#6c757d',
                          mb: 0.5
                        }}
                      >
                        Non-Freeway
                      </Typography>
                      <Box
                        sx={{
                          background: 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)',
                          borderRadius: '8px',
                          px: 1.5,
                          py: 0.3,
                          display: 'inline-block'
                        }}
                      >
                        <Typography
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}
                        >
                          {nonFreewaySpeedLimit} km/h
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Slider
                        value={nonFreewaySpeedLimit}
                        onChange={(_, newValue) =>
                          handleNonFreewaySpeedChange(newValue as number)
                        }
                        min={30}
                        max={100}
                        step={5}
                        size='small'
                        sx={{
                          '& .MuiSlider-track': {
                            background:
                              'linear-gradient(90deg, #ffc107 0%, #fd7e14 100%)',
                            border: 'none'
                          },
                          '& .MuiSlider-thumb': {
                            backgroundColor: '#ffc107',
                            width: 16,
                            height: 16,
                            '&:hover': {
                              boxShadow: '0 0 0 8px rgba(255, 193, 7, 0.16)'
                            }
                          }
                        }}
                      />
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}
                      >
                        <Typography sx={{ fontSize: '9px', color: '#adb5bd' }}>
                          30
                        </Typography>
                        <Typography sx={{ fontSize: '9px', color: '#adb5bd' }}>
                          100
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </>
            ) : (
              ''
            )}

            <CustomSelect
              id='vehicle-type'
              control={control}
              name='vehicleType'
              label='Vehicle Type'
              placeholder='Select vehicle type'
              options={vehicleType}
            />

            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                border: '1px solid #dee2e6',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <Typography
                variant='subtitle2'
                sx={{
                  fontWeight: 600,
                  color: '#495057',
                  fontSize: '13px',
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                🚗 Speed Configuration
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ minWidth: 100 }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#6c757d',
                      mb: 0.5
                    }}
                  >
                    Avg Speed
                  </Typography>
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #fd7e14 0%, #e17055 100%)',
                      borderRadius: '8px',
                      px: 1.5,
                      py: 0.3,
                      display: 'inline-block'
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '12px'
                      }}
                    >
                      {getValues('averageSpeed')} km/h
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Slider
                    value={getValues('averageSpeed')}
                    onChange={(_, newValue) =>
                      setValue('averageSpeed', newValue as number)
                    }
                    min={10}
                    max={150}
                    step={5}
                    size='small'
                    sx={{
                      '& .MuiSlider-track': {
                        background: 'linear-gradient(90deg, #fd7e14 0%, #e17055 100%)',
                        border: 'none'
                      },
                      '& .MuiSlider-thumb': {
                        backgroundColor: '#fd7e14',
                        width: 16,
                        height: 16,
                        '&:hover': {
                          boxShadow: '0 0 0 8px rgba(253, 126, 20, 0.16)'
                        }
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography sx={{ fontSize: '9px', color: '#adb5bd' }}>10</Typography>
                    <Typography sx={{ fontSize: '9px', color: '#adb5bd' }}>
                      150
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {errors.averageSpeed && (
                <Typography
                  sx={{
                    color: '#dc3545',
                    fontSize: '11px',
                    mt: 1,
                    fontWeight: 500
                  }}
                >
                  {errors.averageSpeed.message}
                </Typography>
              )}
            </Box>

            <CustomTextField
              id='absoluteSeating'
              control={control}
              type='number'
              name='absoluteSeating'
              label='Absolute Seating'
              placeholder='Absolute seating'
              onChangeCallback={(e: any) => {
                setAbsoluteSeating(e);
              }}
            />

            <CustomTextField
              id='seating'
              control={control}
              type='number'
              name='seating'
              label='Preferred Seating'
              placeholder='Preferred seating'
              onChangeCallback={(e: any) => {
                const value = Number(e);
                if (isNaN(value) || value < 0 || e === '') {
                  setSeatCapacity(0);
                } else {
                  setSeatCapacity(Math.round((value / absoluteSeating) * 100));
                }
              }}
            />

            <CustomMultiSelect
              id='trip-mode'
              options={mode}
              control={control}
              name='tripMode'
              label='Tour Mode'
              initialValue={getValues('tripMode')?.map((item: any) => item.id)}
              placeholder='Tour mode'
              setValue={setValue}
              width='73%'
              onChanges={(e: any, newValue: any) => {
                if (newValue?.length === 0) {
                  resetField('tripMode');
                }
                trigger('tripMode');
              }}
            />

            <Box
              sx={{
                marginTop: '5%',
                display: 'flex',
                gap: '15px',
                justifyContent: 'end'
              }}
            >
              <CustomButton className='cancel' category='Cancel' onClick={handleClose} />
              <CustomButton
                className='saveChanges'
                type='submit'
                category='Save'
                loading={vehiUpdateLoading || AddVehi}
              />
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default AddVehicle;
